import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, FilePenLine } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { formatNumber } from "../../../../utils";
import { compareTerm } from "../../../RepresentativeOffers/utils";
import { StockStatus } from "../../DepositCard/deposit-dtos";
import { StockStatusBadges } from "../../components/StockStatusBadges";
import { ActivityEvent, ValueChange } from "../types";
import {
  STOCK_HISTORY_EVENT_TYPES,
  actorLabel,
  activityTypeLabelKey,
  eventTime,
  formatDateTime,
  newestFirst,
  reasonText,
} from "../utils";

interface StockHistoryProps {
  events: ActivityEvent[];
  status?: StockStatus | null;
  unit?: string;
  currency: string;
}

const NUMERIC_FIELDS = ["quantity", "price", "total_value"];

export const StockHistory = ({ events, status, unit, currency }: StockHistoryProps) => {
  const { t, i18n } = useTranslation();

  const historyEvents = newestFirst(
    events.filter(event => event.is_this_stock && STOCK_HISTORY_EVENT_TYPES.includes(event.type)),
    eventTime
  );
  if (!historyEvents.length) {
    return null;
  }

  const formatValue = (field: string, value: unknown): string => {
    if (value === null || value === undefined || value === "") {
      return "–";
    }
    if (typeof value === "object") {
      const named = value as { name?: unknown; code?: unknown };
      return String(named.name ?? named.code ?? JSON.stringify(value));
    }
    const text = String(value);
    if (field === "quantity") {
      return unit ? `${formatNumber(text)} ${unit}` : formatNumber(text);
    }
    if (field === "price" || field === "total_value") {
      return `${currency} ${formatNumber(text)}`;
    }
    return text;
  };

  const changeRow = (field: string, change: ValueChange) => {
    const delta = NUMERIC_FIELDS.includes(field)
      ? compareTerm(Number(change.before), Number(change.after))
      : null;
    const DeltaIcon = delta?.direction === "up" ? ArrowUp : ArrowDown;
    return (
      <TableRow key={field}>
        <TableCell className="font-medium">{t(`farmersMarket.history.fields.${field}`)}</TableCell>
        <TableCell>{formatValue(field, change.before)}</TableCell>
        <TableCell>
          <span className="inline-flex items-center gap-1">
            {formatValue(field, change.after)}
            {delta && <DeltaIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />}
          </span>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <FilePenLine className="size-4 text-primary" />
          {t("farmersMarket.history.title")}
          {status && (
            <span className="flex flex-wrap gap-1">
              <StockStatusBadges status={status} />
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {historyEvents.map(event => {
          const changes = Object.entries(event.details.changes ?? {});
          const quantityReason = reasonText(event.details.quantity_reason, i18n.language);
          const priceReason = reasonText(event.details.price_reason, i18n.language);
          const reason = reasonText(event.reason, i18n.language);
          return (
            <div
              key={event.key}
              className="flex flex-col gap-3 rounded-md border border-border p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-semibold text-foreground text-sm">
                  {t(activityTypeLabelKey(event))}
                  {event.details.is_price_adjustment && (
                    <Badge variant="warning">{t("farmersMarket.history.priceAdjusted")}</Badge>
                  )}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatDateTime(eventTime(event))} · {actorLabel(event.actor, t)}
                </span>
              </div>

              {(quantityReason || priceReason || reason) && (
                <dl className="grid gap-1 text-sm sm:grid-cols-[max-content_1fr] sm:gap-x-4">
                  {quantityReason && (
                    <>
                      <dt className="text-muted-foreground">
                        {t("farmersMarket.history.quantityReason")}
                      </dt>
                      <dd>{quantityReason}</dd>
                    </>
                  )}
                  {priceReason && (
                    <>
                      <dt className="text-muted-foreground">
                        {t("farmersMarket.history.priceReason")}
                      </dt>
                      <dd>{priceReason}</dd>
                    </>
                  )}
                  {reason && (
                    <>
                      <dt className="text-muted-foreground">{t("farmersMarket.history.reason")}</dt>
                      <dd>{reason}</dd>
                    </>
                  )}
                </dl>
              )}

              {event.comments && (
                <p className="text-muted-foreground text-sm">
                  {t("farmersMarket.history.comments")}: {event.comments}
                </p>
              )}

              {changes.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("farmersMarket.history.field")}</TableHead>
                      <TableHead>{t("farmersMarket.history.previous")}</TableHead>
                      <TableHead>{t("farmersMarket.history.updated")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changes.map(([field, change]) => changeRow(field, change))}
                  </TableBody>
                </Table>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
